import { useState } from 'react'
import type { FormEvent } from 'react'
import {
  useCreateSubscriptionPlan,
  useSettings,
  useSubscriptionPlans,
  useUpdateSubscriptionPlan,
} from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { SelectField, TextField } from '@/components/ui/Field'
import { Pill } from '@/components/ui/Pill'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { formatMoney } from '@/lib/format'
import type { SubscriptionPlan, SubscriptionPlanInput } from '@/types/api'
import styles from './PlansPage.module.css'

/**
 * Subscription plans.
 *
 * *** A PLAN IS A SESSION ALLOWANCE PLUS ITS OWN CHAT RULES ***
 * Chat is what separates a basic tier from a premium one, so it is configured
 * here rather than on the tenant-wide Chat Master. Anything left blank falls
 * back to the Chat Master, which is why every chat field is optional.
 *
 * *** EDITING A PLAN DOES NOT CHANGE EXISTING SUBSCRIBERS ***
 * The allowance is snapshotted when a student is assigned the plan. Raising a
 * plan from 8 to 12 sessions does not top up everyone who already bought it —
 * said plainly on the form, because the opposite is a reasonable assumption.
 */
export function PlansPage() {
  const toast = useToast()
  const settings = useSettings()
  const { data, isLoading, error, refetch } = useSubscriptionPlans()
  const createMutation = useCreateSubscriptionPlan()
  const updateMutation = useUpdateSubscriptionPlan()

  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<SubscriptionPlan | null>(null)

  /**
   * The plan's answer to "what happens when this plan expires", or '' to
   * inherit the Chat policy.
   *
   * *** CONTROLLED, UNLIKE EVERY OTHER FIELD ON THIS FORM ***
   * The day count below only appears while this is GRACE, so the dialog has to
   * re-render when it changes. It is reset whenever the dialog opens: the
   * dialog stays mounted, so a value left from the last plan edited would
   * otherwise be shown as though it belonged to this one.
   */
  const [expiry, setExpiry] = useState<string>('')

  const plans = data?.plans ?? []
  const open = creating || editing !== null

  // Only once settings have loaded — assuming per-session while the request is
  // in flight would flash a warning at a subscription client on every visit.
  const isPerSession = settings.data?.billing_mode === 'PER_SESSION'

  // Seeded in one place rather than at each call site, so a third way to open
  // the dialog cannot forget to reset it.
  const openCreate = () => {
    setExpiry('')
    setCreating(true)
  }

  const openEdit = (plan: SubscriptionPlan) => {
    setExpiry(plan.plan_expiry_behaviour ?? '')
    setEditing(plan)
  }

  const closeForm = () => {
    setCreating(false)
    setEditing(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    const text = (key: string) => String(form.get(key) ?? '').trim()
    /** Empty means "not set" — it must reach the server as null, not 0. */
    const optionalNumber = (key: string) => {
      const raw = text(key)
      return raw === '' ? null : Number(raw)
    }

    const input: SubscriptionPlanInput = {
      name: text('name'),
      description: text('description') || null,
      price: text('price'),
      session_count: Number(text('session_count')),
      validity_days: optionalNumber('validity_days'),
      cancellation_rule: text('cancellation_rule') as SubscriptionPlanInput['cancellation_rule'],
      free_chat_mode: text('free_chat_mode') || null,
      free_window_hours: optionalNumber('free_window_hours'),
      free_message_limit: optionalNumber('free_message_limit'),
      upsell_message: text('upsell_message') || null,
      display_order: Number(text('display_order') || 0),
      // null = inherit the Chat policy, the same convention as every chat
      // field above. Sent explicitly so an override can be cleared again.
      plan_expiry_behaviour:
        (expiry as SubscriptionPlanInput['plan_expiry_behaviour']) || null,
      plan_expiry_grace_days:
        expiry === 'GRACE' ? optionalNumber('plan_expiry_grace_days') : null,
    }

    // *** GRACE WITH NO NUMBER MEANS NO GRACE AT ALL ***
    // The server refuses it; caught here so the admin reads a question rather
    // than a validation failure.
    if (expiry === 'GRACE' && !input.plan_expiry_grace_days) {
      toast.error('How many days should chat continue after this plan ends?')
      return
    }

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.plan_id, input })
        toast.success(`${input.name} updated.`)
      } else {
        await createMutation.mutateAsync(input)
        toast.success(`${input.name} created.`)
      }
      closeForm()
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save the plan.',
      )
    }
  }

  const columns: Array<Column<SubscriptionPlan>> = [
    {
      key: 'name',
      header: 'Plan',
      render: (row) => (
        <div>
          <div className={styles.planName}>{row.name}</div>
          {row.description && (
            <div className={styles.muted}>{row.description}</div>
          )}
        </div>
      ),
    },
    {
      key: 'sessions',
      header: 'Sessions',
      align: 'right',
      render: (row) => <span className="numeric">{row.session_count}</span>,
    },
    {
      key: 'validity',
      header: 'Validity',
      render: (row) =>
        row.validity_days === null ? (
          // Worth spelling out: an empty cell would read as misconfigured.
          <span className={styles.muted}>Until sessions run out</span>
        ) : (
          <span>{row.validity_days} days</span>
        ),
    },
    {
      key: 'price',
      header: 'Price',
      align: 'right',
      render: (row) => (
        <span className="numeric">{formatMoney(row.price, row.currency)}</span>
      ),
    },
    {
      key: 'cancellation',
      header: 'Cancelled sessions',
      secondary: true,
      render: (row) => <span className={styles.muted}>{RULE_LABELS[row.cancellation_rule]}</span>,
    },
    {
      key: 'subscribers',
      header: 'Students',
      align: 'right',
      secondary: true,
      render: (row) => <span className="numeric">{row.subscriber_count}</span>,
    },
    {
      key: 'status',
      header: '',
      align: 'right',
      render: (row) =>
        row.is_active ? (
          <Pill tone="success">Active</Pill>
        ) : (
          <Pill tone="neutral">Inactive</Pill>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '110px',
      render: (row) => (
        <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
          Edit
        </Button>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Subscription plans"
        description="What students buy. Each plan includes a number of sessions and its own chat rules."
        actions={
          <Button variant="primary" onClick={() => openCreate()}>
            Add plan
          </Button>
        }
      />

      {/*
        *** WHY THIS PAGE EXISTS ON A PER-SESSION CLIENT ***
        Plans are inert until billing is switched, but the page stays visible
        so they can be created FIRST. Hiding it until after the switch would
        mean every student is blocked from booking while the admin scrambles
        to create the first plan. Said plainly, because an unexplained screen
        that does nothing is worse than no screen at all.
      */}
      {isPerSession && (
        <Callout tone="warning" title="This organisation bills per session">
          Students are charged for each booking, so plans created here are not
          in use yet. You can set them up now — they take effect only once the
          billing model is switched to subscription, which is done by the
          platform team.
        </Callout>
      )}

      {plans.length === 0 && !isLoading && !isPerSession && (
        <Callout tone="info" title="No plans yet">
          Students need a plan before they can book. Create one to get started.
        </Callout>
      )}

      <Card>
        <Table
          columns={columns}
          rows={plans}
          rowKey={(row) => row.plan_id}
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          emptyMessage="No subscription plans."
        />
      </Card>

      <Modal
        open={open}
        onClose={closeForm}
        title={editing ? `Edit ${editing.name}` : 'New subscription plan'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className={styles.section}>
            <TextField
              label="Plan name"
              name="name"
              defaultValue={editing?.name ?? ''}
              required
              maxLength={150}
            />
            <TextField
              label="Description"
              name="description"
              defaultValue={editing?.description ?? ''}
              maxLength={1000}
              hint="Shown to the admin only."
            />

            <div className={styles.grid}>
              <TextField
                label="Price"
                name="price"
                type="number"
                min={0}
                step={0.01}
                defaultValue={editing?.price ?? ''}
                required
              />
              <TextField
                label="Sessions included"
                name="session_count"
                type="number"
                min={1}
                max={1000}
                defaultValue={editing?.session_count ?? ''}
                required
                hint="Meetings the student can book."
              />
            </div>

            <div className={styles.grid}>
              <TextField
                label="Validity (days)"
                name="validity_days"
                type="number"
                min={1}
                max={3650}
                defaultValue={editing?.validity_days ?? ''}
                hint="Leave blank for no expiry — the plan lasts until the sessions run out."
              />
              <SelectField
                label="Cancelled sessions"
                name="cancellation_rule"
                defaultValue={editing?.cancellation_rule ?? 'RETURN'}
                options={[
                  { value: 'RETURN', label: 'Return to the student' },
                  { value: 'CONSUME', label: 'Count as used' },
                  { value: 'FOLLOW_CUTOFF', label: 'Follow the cancellation cutoff' },
                ]}
                hint="“Follow the cutoff” reuses the rule in Settings instead of setting a second one here."
              />
            </div>
          </div>

          <div className={styles.divider} />

          {/*
            *** BLANK MEANS "USE THE CHAT MASTER" ***
            Said explicitly, because a blank number field otherwise reads as
            zero — which would mean "no messages at all".
          */}
          <p className={styles.sectionTitle}>Chat for this plan</p>
          <p className={styles.sectionHint}>
            Leave anything blank to use the tenant’s Chat policy instead.
          </p>

          <div className={styles.section}>
            <div className={styles.grid}>
              <SelectField
                label="Free chat"
                name="free_chat_mode"
                defaultValue={editing?.free_chat_mode ?? ''}
                options={[
                  { value: '', label: 'Use Chat policy' },
                  { value: 'PERMANENT', label: 'Always available' },
                  { value: 'AFTER_SESSION', label: 'After a session' },
                  { value: 'DISABLED', label: 'Disabled' },
                ]}
              />
              <TextField
                label="Message limit"
                name="free_message_limit"
                type="number"
                min={0}
                defaultValue={editing?.free_message_limit ?? ''}
                hint="Blank = use Chat policy."
              />
            </div>

            <div className={styles.grid}>
              <TextField
                label="Chat window (hours)"
                name="free_window_hours"
                type="number"
                min={0}
                defaultValue={editing?.free_window_hours ?? ''}
                hint="How long chat stays open after a session."
              />
              <TextField
                label="Display order"
                name="display_order"
                type="number"
                min={0}
                defaultValue={editing?.display_order ?? 0}
              />
            </div>

            <div className={styles.grid}>
              {/*
                *** A RETENTION LEVER, WHICH IS WHY IT IS PER PLAN ***
                A premium plan can keep chat open after it lapses while the
                basic one cuts off, so the tiers differ in something a student
                actually feels. Blank inherits the Chat policy, like the rest.
              */}
              <SelectField
                label="When this plan expires"
                name="plan_expiry_behaviour"
                value={expiry}
                onChange={(event) => setExpiry(event.target.value)}
                options={[
                  { value: '', label: 'Use Chat policy' },
                  { value: 'REQUIRE_PLAN', label: 'Chat stops until they renew' },
                  { value: 'GRACE', label: 'Chat continues for a while' },
                  {
                    value: 'KEEP_UNLOCKED',
                    label: 'Keep existing conversations open',
                  },
                  { value: 'FALL_BACK', label: 'Treat them as a free user' },
                ]}
                hint="Overrides the Chat policy for students on this plan."
              />

              {expiry === 'GRACE' && (
                <TextField
                  label="Grace period (days)"
                  name="plan_expiry_grace_days"
                  type="number"
                  min={1}
                  max={365}
                  required
                  defaultValue={editing?.plan_expiry_grace_days ?? ''}
                  hint="How long chat keeps working after this plan ends."
                />
              )}
            </div>

            <TextField
              label="Upsell message"
              name="upsell_message"
              defaultValue={editing?.upsell_message ?? ''}
              maxLength={500}
              hint="Shown to a student when their chat is exhausted."
            />
          </div>

          {editing && editing.subscriber_count > 0 && (
            <Callout tone="warning" title="Existing subscribers are unaffected">
              {editing.subscriber_count} student
              {editing.subscriber_count === 1 ? '' : 's'} already hold this plan.
              Their session allowance was fixed when it was assigned, so changes
              here apply only to students assigned the plan from now on.
            </Callout>
          )}

          <div className={styles.formActions}>
            <Button type="button" variant="secondary" onClick={closeForm}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {editing ? 'Save changes' : 'Create plan'}
            </Button>
          </div>
        </form>
      </Modal>
    </PageStack>
  )
}

const RULE_LABELS: Record<string, string> = {
  RETURN: 'Returned',
  CONSUME: 'Counted as used',
  FOLLOW_CUTOFF: 'Follows the cutoff',
}
